import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { AuthProvider, useApiAuth } from '@/services/auth';
import { Router } from './Router';
import { ErrorBoundary, DefaultErrorFallback } from '@/logging/ErrorBoundary';
import { initializeLogging } from '@/logging';
import { sseService } from '@/services/sse';
import { AuthorizationCheck } from '@/components/auth';

// Initialize logging system on module load
initializeLogging();

// Component that sets up API authentication
const AuthenticatedApp: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize API authentication
  useApiAuth();

  // Connect SSE once authenticated app mounts
  useEffect(() => {
    console.log('🚀 AuthenticatedApp mounting - attempting SSE connection (NO AUTH)');
    console.log('Environment variables:', {
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      VITE_API_URL: import.meta.env.VITE_API_URL,
      NODE_ENV: import.meta.env.NODE_ENV,
      MODE: import.meta.env.MODE,
    });

    try {
      sseService.connect();
      console.log('✅ SSE service connect() called successfully (NO AUTH)');
    } catch (error) {
      console.error('❌ Failed to call sseService.connect():', error);
    }

    return () => {
      console.log('🔌 AuthenticatedApp unmounting - disconnecting SSE');
      sseService.disconnect();
    };
  }, []);

  return <>{children}</>;
};

export const App: React.FC = () => {
  return (
    <ErrorBoundary fallback={DefaultErrorFallback}>
      <FluentProvider theme={webLightTheme}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <AuthenticatedApp>
              <AuthorizationCheck>
                <Router />
              </AuthorizationCheck>
            </AuthenticatedApp>
          </AuthProvider>
        </BrowserRouter>
      </FluentProvider>
    </ErrorBoundary>
  );
};
